import {
    isValidElement,
    useContext,
    useEffect,
    type FC,
    useId
} from "react";
import {
    type LayoutChangeEvent,
    Platform,
    View
} from "react-native";
import type {
    TableCellContentProps,
    TableCellBaseProps
} from "./type";
import stylesheet, {
    getTableCellRole,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    TableSectionContext,
    TableRowContext,
    useTableContext
} from "../../context";
import {
    getDirectionSign
} from "../../../../helpers/direction";
import StateLayer from "../../../stateLayer";
import Text from "../../../text";

const TableCellContent: FC<TableCellContentProps> = ({
    customTheme: customThemeProp,
    isSticky: isStickyProp,
    isNumeric = false,
    isShrink = false,
    stickyOffset = 0,
    sortDirection,
    colSpan = 1,
    children,
    context,
    align,
    scope,
    style,
    kind,
    ...props
}) => {
    const {
        columnIndex,
        isDisabled,
        isSelected,
        isHovered,
        isPressed
    } = useContext(TableRowContext);

    const {
        section
    } = useContext(TableSectionContext);

    const {
        shapeConfigs,
        shadowAlpha,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customThemeProp ?? context.customTheme);

    const {
        direction
    } = NCoreUIKitLocalize.useContext();

    const cellID = useId();

    const isHead = kind === "head";

    const isShrinkMeasured = isShrink && colSpan === 1;

    const isSticky = isStickyProp ?? (context.isFirstColumnSticky && columnIndex === 0);

    const currentAlign = align ?? (isNumeric ? "end" : "start");

    const {
        container: containerDynamicStyle,
        text: textDynamicStyle
    } = useStyles({
        background: isSelected ? colors.content.container.selected : context.tableBackground,
        shrinkWidth: isShrinkMeasured ? context.shrinkWidths[columnIndex] : undefined,
        isEdgeShadowVisible: context.isScrolledFromStart,
        isSticky: isSticky && Platform.OS === "web",
        directionSign: getDirectionSign({
            direction
        }),
        layers: shapeConfigs.layers,
        measures: context.measures,
        align: currentAlign,
        stickyOffset,
        shadowAlpha,
        isNumeric,
        isShrink,
        borders,
        colSpan,
        colors,
        spaces,
        isHead
    });

    const currentVariant = isHead || section === "footer" ? context.measures.headVariant : context.measures.bodyVariant;

    const currentColor = isDisabled || (isHead && !sortDirection) ? "mid" : "high";

    const currentRole = getTableCellRole({
        scope,
        kind
    });

    const ariaProps: Record<string, string | number | undefined> = {
        "aria-colspan": colSpan > 1 ? colSpan : undefined,
        "aria-sort": sortDirection
    };

    useEffect(() => {
        if(!isShrinkMeasured) {
            return;
        }

        return () => {
            context.reportShrinkWidth(columnIndex, cellID);
        };
    }, [
        isShrinkMeasured,
        columnIndex,
        cellID
    ]);

    const handleShrinkLayout = (event: LayoutChangeEvent) => {
        context.reportShrinkWidth(columnIndex, cellID, Math.ceil(event.nativeEvent.layout.width + context.measures.cellPadding * 2));
    };

    const renderChildren = () => {
        if(isValidElement(children) || (typeof children !== "string" && typeof children !== "number")) {
            return children;
        }

        return <Text
            customTheme={customThemeProp ?? context.customTheme}
            variant={currentVariant}
            style={[
                stylesheet.text,
                textDynamicStyle
            ]}
            color={currentColor}
            numberOfLines={1}
        >
            {children}
        </Text>;
    };

    const renderContent = () => {
        if(!isShrinkMeasured) {
            return renderChildren();
        }

        return <View
            onLayout={handleShrinkLayout}
        >
            {renderChildren()}
        </View>;
    };

    return <View
        {...props}
        {...ariaProps}
        style={[
            style,
            stylesheet.container,
            isShrink ? stylesheet.shrink : stylesheet.fill,
            containerDynamicStyle
        ]}
        role={currentRole}
    >
        {
            isSticky && Platform.OS === "web" ?
                <StateLayer
                    customTheme={customThemeProp ?? context.customTheme}
                    overlayColor="default"
                    isHovered={isHovered}
                    isPressed={isPressed}
                    borderRadius={0}
                />
            :
                null
        }
        {renderContent()}
    </View>;
};

const TableCellBase: FC<TableCellBaseProps> = (props) => {
    const context = useTableContext({
        part: props.kind === "head" ? "Head" : "Cell"
    });

    if(!context) {
        return null;
    }

    return <TableCellContent
        {...props}
        context={context}
    />;
};
export default TableCellBase;
